import { TFunction, programAllows } from "utils";

import { CarbonProgram } from "models";

export enum RAIL_NAV_KEYS {
  HOME = "HOME",
  FIELDS_OVERVIEW = "FIELDS_OVERVIEW",
  CARBON_FOOTPRINTING = "CARBON_FOOTPRINTING",
  PAYOUT = "PAYOUT",
  EXPLORE_SCENARIOS = "EXPLORE_SCENARIOS",
  CREATE_COVER_CROP_MIXES = "CREATE_COVER_CROP_MIXES",
  CHAT_WITH_TERRA = "CHAT_WITH_TERRA",
}

export type RailNavItem = {
  key: RAIL_NAV_KEYS;
  label: string;
  iconName: string;
  to: string;
  state?: Record<string, unknown>;
  tag?: string;
  dataTestId: string;
};

export type RailNavGroup = {
  key: "main" | "myFarm" | "tools";
  heading?: string;
  items: RailNavItem[];
};

type BuildRailNavGroupsParams = {
  farmOnboarded: boolean;
  farmResolved: boolean;
  carbonProgram?: CarbonProgram;
  isSoilSightEnabled: boolean;
  isCopilotEnabled: boolean;
  t: TFunction;
};

/** Items follow the farm's program. */
export const buildRailNavGroups = ({
  farmOnboarded,
  farmResolved,
  carbonProgram,
  isSoilSightEnabled,
  isCopilotEnabled,
  t,
}: BuildRailNavGroupsParams): RailNavGroup[] => {
  const mainGroup: RailNavGroup = {
    key: "main",
    items: [{ key: RAIL_NAV_KEYS.HOME, label: t("railNav.home"), iconName: "tabler/home", to: "/home", dataTestId: "rail-nav-home" }],
  };

  // only Home until the farm's program is known, so restricted farms never see items they cannot use
  if (!farmResolved) return [mainGroup];

  const myFarmItems: RailNavItem[] = farmOnboarded
    ? [
        ...(programAllows(carbonProgram, "fields")
          ? [
              {
                key: RAIL_NAV_KEYS.FIELDS_OVERVIEW,
                label: t("railNav.fieldsOverview"),
                iconName: "klim/field",
                to: "/all-fields",
                dataTestId: "rail-nav-fields-overview",
              },
            ]
          : []),
        ...(programAllows(carbonProgram, "footprinting")
          ? [
              {
                key: RAIL_NAV_KEYS.CARBON_FOOTPRINTING,
                label: t("railNav.carbonFootprinting"),
                iconName: "klim/crop-footprint",
                to: "/footprinting",
                dataTestId: "rail-nav-carbon-footprinting",
              },
            ]
          : []),
        ...(programAllows(carbonProgram, "payout")
          ? [{ key: RAIL_NAV_KEYS.PAYOUT, label: t("railNav.farm.payout"), iconName: "tabler/wallet", to: "/wallet", dataTestId: "rail-nav-payout" }]
          : []),
      ]
    : [];

  const toolItems: RailNavItem[] = [
    ...(isSoilSightEnabled
      ? [
          {
            key: RAIL_NAV_KEYS.EXPLORE_SCENARIOS,
            label: t("railNav.exploreScenarios"),
            iconName: "klim/crop-rotation",
            to: "/scenarios-landing-page",
            state: { entryPoint: "nav" },
            tag: t("new").toUpperCase(),
            dataTestId: "rail-nav-explore-scenarios",
          },
        ]
      : []),
    ...(programAllows(carbonProgram, "cropMixComposer")
      ? [
          {
            key: RAIL_NAV_KEYS.CREATE_COVER_CROP_MIXES,
            label: t("railNav.createCoverCropMixes"),
            iconName: "klim/plant-ai",
            to: "/cmc",
            dataTestId: "rail-nav-create-cover-crop-mixes",
          },
        ]
      : []),
    ...(isCopilotEnabled
      ? [
          {
            key: RAIL_NAV_KEYS.CHAT_WITH_TERRA,
            label: t("railNav.chatWithTerra"),
            iconName: "klim/magic",
            to: "/copilot",
            tag: t("beta").toUpperCase(),
            dataTestId: "rail-nav-chat-with-terra",
          },
        ]
      : []),
  ];

  const groups: RailNavGroup[] = [mainGroup];

  if (myFarmItems.length) groups.push({ key: "myFarm", heading: t("railNav.myFarm"), items: myFarmItems });
  if (toolItems.length) groups.push({ key: "tools", heading: t("railNav.toolsToImprove"), items: toolItems });

  return groups;
};

const isPathOrChild = (pathname: string, base: string) => pathname === base || pathname.startsWith(`${base}/`);

export const getRailNavActiveKey = (pathname: string): RAIL_NAV_KEYS | null => {
  if (!pathname) return null;

  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

  if (path === "/home") return RAIL_NAV_KEYS.HOME;
  if (isPathOrChild(path, "/all-fields")) return RAIL_NAV_KEYS.FIELDS_OVERVIEW;
  if (isPathOrChild(path, "/footprinting")) return RAIL_NAV_KEYS.CARBON_FOOTPRINTING;
  if (path === "/wallet") return RAIL_NAV_KEYS.PAYOUT;
  if (path.startsWith("/scenarios")) return RAIL_NAV_KEYS.EXPLORE_SCENARIOS;
  if (isPathOrChild(path, "/cmc")) return RAIL_NAV_KEYS.CREATE_COVER_CROP_MIXES;
  if (path === "/copilot") return RAIL_NAV_KEYS.CHAT_WITH_TERRA;

  return null;
};
